"use client";

import React from "react";
import { motion } from "framer-motion";
import { twMerge } from "tailwind-merge";

export interface CardProps {
  children: React.ReactNode;
  as?: "div" | "section" | "article";
  hoverable?: boolean;
  animate?: boolean;
  delay?: number;
  className?: string;
  onClick?: () => void;
  id?: string;
  role?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
}

export function Card({
  children,
  as = "div",
  hoverable = false,
  animate = true,
  delay = 0,
  className = "",
  ...props
}: CardProps) {
  const baseClassName = twMerge(
    `bg-white rounded-2xl border border-gray-100/50 shadow-sm p-8 sm:p-10 ${
      hoverable ? "cursor-pointer" : ""
    }`,
    className,
  );

  if (!animate) {
    const Component = as;
    return (
      <Component
        className={twMerge(
          baseClassName,
          "transition-all duration-200",
          hoverable ? "hover:shadow-md" : "",
        )}
        {...props}
      >
        {children}
      </Component>
    );
  }

  const MotionComponent = motion[as] as typeof motion.div;

  return (
    <MotionComponent
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.4,
        delay,
        ease: [0.25, 0.1, 0.25, 1],
      }}
      whileHover={
        hoverable
          ? {
              y: -4,
              transition: { duration: 0.2, ease: "easeOut" },
            }
          : undefined
      }
      className={twMerge(
        baseClassName,
        "transition-shadow duration-200",
        hoverable ? "hover:shadow-md" : "",
      )}
      onClick={props.onClick}
      id={props.id}
      role={props.role}
      aria-label={props["aria-label"]}
      aria-labelledby={props["aria-labelledby"]}
      aria-describedby={props["aria-describedby"]}
    >
      {children}
    </MotionComponent>
  );
}

export default Card;
